import type { ClipboardEvent, DragEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CHAT_ATTACHMENT_ACCEPT } from '../components/PendingAttachmentList';

// Dropped and picked files can bypass the file input's `accept` filter (it is advisory in the
// OS dialog), so the extension gate is re-applied here.
const ACCEPTED_EXTENSIONS = new Set(CHAT_ATTACHMENT_ACCEPT.split(','));

export const hasAcceptedExtension = (file: File): boolean => {
    const lastDotIndex = file.name.lastIndexOf('.');
    return lastDotIndex > 0 && ACCEPTED_EXTENSIONS.has(file.name.slice(lastDotIndex).toLowerCase());
};

const isFileDrag = (event: DragEvent<HTMLElement>): boolean => event.dataTransfer.types.includes('Files');

export type UseAttachmentDropReturn = {
    readonly isDragActive: boolean;
    readonly dropHandlers: {
        readonly onDragEnter: (event: DragEvent<HTMLElement>) => void;
        readonly onDragLeave: (event: DragEvent<HTMLElement>) => void;
        readonly onDragOver: (event: DragEvent<HTMLElement>) => void;
        readonly onDrop: (event: DragEvent<HTMLElement>) => void;
        readonly onPaste: (event: ClipboardEvent<HTMLElement>) => void;
    };
};

export const useAttachmentDrop = ({
    enabled,
    onFiles,
}: {
    readonly enabled: boolean;
    readonly onFiles: (files: readonly File[]) => void;
}): UseAttachmentDropReturn => {
    const [isDragActive, setIsDragActive] = useState(false);
    // dragenter/dragleave fire for every child boundary crossed; the depth counter keeps the
    // highlight stable until the pointer leaves the container for real.
    const dragDepthRef = useRef(0);

    // The handlers ignore events while disabled, so a mid-drag disable would leave the
    // highlight stuck on without this reset.
    useEffect(() => {
        if (enabled) return;
        dragDepthRef.current = 0;
        setIsDragActive(false);
    }, [enabled]);

    const onDragEnter = useCallback(
        (event: DragEvent<HTMLElement>) => {
            if (!enabled || !isFileDrag(event)) return;
            event.preventDefault();
            dragDepthRef.current += 1;
            setIsDragActive(true);
        },
        [enabled],
    );

    const onDragLeave = useCallback(
        (event: DragEvent<HTMLElement>) => {
            if (!enabled || !isFileDrag(event)) return;
            event.preventDefault();
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0) setIsDragActive(false);
        },
        [enabled],
    );

    const onDragOver = useCallback(
        (event: DragEvent<HTMLElement>) => {
            if (!enabled || !isFileDrag(event)) return;
            event.preventDefault();
        },
        [enabled],
    );

    const onDrop = useCallback(
        (event: DragEvent<HTMLElement>) => {
            if (!enabled || !isFileDrag(event)) return;
            event.preventDefault();
            dragDepthRef.current = 0;
            setIsDragActive(false);
            const files = Array.from(event.dataTransfer.files).filter(hasAcceptedExtension);
            if (files.length > 0) onFiles(files);
        },
        [enabled, onFiles],
    );

    // The default paste is suppressed only when at least one accepted file gets attached — a
    // text paste, or one carrying only unsupported files, keeps its normal behavior.
    const onPaste = useCallback(
        (event: ClipboardEvent<HTMLElement>) => {
            if (!enabled) return;
            const files = Array.from(event.clipboardData.files).filter(hasAcceptedExtension);
            if (files.length === 0) return;
            event.preventDefault();
            onFiles(files);
        },
        [enabled, onFiles],
    );

    return { dropHandlers: { onDragEnter, onDragLeave, onDragOver, onDrop, onPaste }, isDragActive };
};
